import { Box } from "@mui/material"
import { If, Else } from "lib/@components/switch"
import { EmptyValue, isEmptyValue } from "./requested-field-value-empty"

/**
 * Read-only display of a `richText` requested field's answer.
 *
 * The stored value is editor HTML. Every HTML string written to the database is sanitised at the
 * persistence boundary — `LocalDatabasePut("*")` runs `sanitizeObject` (DOMPurify: strips
 * `script`/event-handler attributes/`iframe`/etc.) on every row put/update, see
 * `api/src/modules/sanitizer/sanitize-html.module.mjs`. The persisted value is therefore safe to
 * render as HTML, which is exactly how the app shows other stored rich text read-only (task
 * content/notes, facility/space descriptions all use `dangerouslySetInnerHTML`).
 *
 * @param {object} props
 * @param {{ id: string }} props.field
 * @param {string} [props.value]
 */
export function RichTextFieldValue({ value }) {
    return (
        <If value={hasVisibleHtmlContent(value)}>
            <Box
                data-cy="requested-field-value-rich-text"
                sx={{
                    typography: "body2",
                    wordBreak: "break-word",
                    "& > :first-of-type": { mt: 0 },
                    "& > :last-child": { mb: 0 },
                }}
                dangerouslySetInnerHTML={{ __html: value ?? "" }}
            />
            <Else>
                <EmptyValue />
            </Else>
        </If>
    )
}

// True when the stored HTML has visible text once tags/entities are stripped. Used ONLY to choose
// between rendering the answer and showing the muted "no answer" placeholder (an empty editor stores
// markup such as "<p></p>" / "<p><br></p>"). The value itself is rendered as sanitised HTML via
// dangerouslySetInnerHTML above — this helper never feeds the render path.
function hasVisibleHtmlContent(value) {
    if (isEmptyValue(value)) return false
    if (typeof value !== "string") return true
    const text = value
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/gi, " ")
        .replace(/&amp;/gi, "&")
        .replace(/&lt;/gi, "<")
        .replace(/&gt;/gi, ">")
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'")
        .trim()
    return text !== ""
}
